import { useEffect, useState } from 'react';
import financasApi from '@/lib/financasApi';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { CheckCircle2, AlertCircle, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const fmtBRL = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtDate = (d) => d ? new Date(d + 'T00:00:00').toLocaleDateString('pt-BR') : '-';

function isAtrasado(item) {
  const ref = item.data_vencimento || item.data;
  if (!ref) return false;
  const hoje = new Date().toISOString().slice(0, 10);
  return ref < hoje && item.status === 'pendente';
}

function ContasTab({ tipo }) {
  const [items, setItems] = useState([]);

  const load = async () => {
    const params = new URLSearchParams({ tipo });
    const { data } = await financasApi.get(`/financas/transacoes?${params.toString()}`);
    setItems(data.filter(i => i.status !== 'pago'));
  };

  useEffect(() => { load(); }, [tipo]);

  const marcarPago = async (item) => {
    try {
      await financasApi.put(`/financas/transacoes/${item.id}`, { status: 'pago' });
      toast.success('Marcado como pago');
      load();
    } catch {
      toast.error('Erro ao atualizar');
    }
  };

  const total = items.reduce((s, i) => s + i.valor, 0);
  const corTotal = tipo === 'entrada' ? 'emerald' : 'red';

  return (
    <div className="space-y-3">
      <div className={`bg-slate-900 border border-${corTotal}-500/30 rounded-xl p-4 flex justify-between items-center`}>
        <div>
          <p className="text-xs text-slate-400 uppercase tracking-wider">Total {tipo === 'entrada' ? 'a Receber' : 'a Pagar'}</p>
          <p className={`text-2xl font-bold text-${corTotal}-400 mt-1`}>{fmtBRL(total)}</p>
        </div>
        <div className="text-right text-sm text-slate-400">
          <p>{items.length} pendência(s)</p>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-12 text-slate-500 bg-slate-900 border border-white/10 rounded-xl">
          <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-emerald-500/50" />
          Nenhuma pendência
        </div>
      ) : (
        <>
          {/* Cards mobile */}
          <div className="md:hidden space-y-2">
            {items.map(item => {
              const atrasado = isAtrasado(item);
              return (
                <div key={item.id} className={`bg-slate-900 border rounded-xl p-3 ${atrasado ? 'border-red-500/30' : 'border-white/10'}`}>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-white text-sm font-medium truncate">{item.descricao}</p>
                      <p className="text-xs text-slate-500 truncate mt-0.5">{item.cliente || item.fornecedor || '-'}</p>
                      <div className="flex items-center gap-1.5 text-xs mt-1">
                        {atrasado && <AlertCircle className="w-3.5 h-3.5 text-red-400" />}
                        <Calendar className="w-3 h-3 text-slate-500" />
                        <span className={atrasado ? 'text-red-400' : 'text-slate-400'}>{fmtDate(item.data_vencimento || item.data)}</span>
                      </div>
                    </div>
                    <p className={`font-semibold text-${corTotal}-400 whitespace-nowrap text-sm`}>{fmtBRL(item.valor)}</p>
                  </div>
                  <Button size="sm" onClick={() => marcarPago(item)} className="w-full bg-emerald-600 hover:bg-emerald-500 text-white" data-testid={`financas-pagar-${item.id}`}>
                    <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> {tipo === 'entrada' ? 'Marcar como Recebido' : 'Marcar como Pago'}
                  </Button>
                </div>
              );
            })}
          </div>

          {/* Tabela desktop */}
          <div className="hidden md:block bg-slate-900 border border-white/10 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
            <thead className="bg-slate-800/50">
              <tr className="text-left text-slate-400 text-xs uppercase tracking-wider">
                <th className="px-4 py-3">Vencimento</th>
                <th className="px-4 py-3">Descrição</th>
                <th className="px-4 py-3">{tipo === 'entrada' ? 'Cliente' : 'Fornecedor'}</th>
                <th className="px-4 py-3 text-right">Valor</th>
                <th className="px-4 py-3 text-right">Ação</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {items.map(item => {
                const atrasado = isAtrasado(item);
                return (
                  <tr key={item.id} className={`hover:bg-white/5 ${atrasado ? 'bg-red-500/5' : ''}`}>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {atrasado && <AlertCircle className="w-4 h-4 text-red-400" />}
                        <Calendar className="w-3.5 h-3.5 text-slate-500" />
                        <span className={atrasado ? 'text-red-400' : 'text-slate-300'}>{fmtDate(item.data_vencimento || item.data)}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-white">{item.descricao}</td>
                    <td className="px-4 py-3 text-slate-400">{item.cliente || item.fornecedor || '-'}</td>
                    <td className={`px-4 py-3 text-right font-semibold text-${corTotal}-400`}>{fmtBRL(item.valor)}</td>
                    <td className="px-4 py-3 text-right">
                      <Button size="sm" onClick={() => marcarPago(item)} className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid={`financas-pagar-${item.id}`}>
                        <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> {tipo === 'entrada' ? 'Recebido' : 'Pago'}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </>
      )}
    </div>
  );
}

export default function FinancasContas() {
  return (
    <div className="p-4 md:p-8 max-w-[1400px] mx-auto" data-testid="financas-contas-page">
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-white">Contas a Pagar / Receber</h1>
        <p className="text-slate-400 text-xs md:text-sm mt-1">Acompanhe pendências e vencimentos</p>
      </div>
      <Tabs defaultValue="receber" className="w-full">
        <TabsList className="bg-slate-900 border border-white/10">
          <TabsTrigger value="receber" data-testid="financas-tab-receber" className="data-[state=active]:bg-emerald-500/20 data-[state=active]:text-emerald-300">A Receber</TabsTrigger>
          <TabsTrigger value="pagar" data-testid="financas-tab-pagar" className="data-[state=active]:bg-red-500/20 data-[state=active]:text-red-300">A Pagar</TabsTrigger>
        </TabsList>
        <TabsContent value="receber" className="mt-4"><ContasTab tipo="entrada" /></TabsContent>
        <TabsContent value="pagar" className="mt-4"><ContasTab tipo="saida" /></TabsContent>
      </Tabs>
    </div>
  );
}
